import React, {useState, useContext, useRef} from 'react'
import { useNavigate } from 'react-router-dom'
import { Row, Col, Button, Spin } from "antd";
import { LoadingOutlined, CheckCircleOutlined, RightSquareFilled, LeftSquareFilled, CarryOutFilled, CloseCircleOutlined } from '@ant-design/icons';
import { AppContext } from '../../util/context';
import { uploadData } from 'aws-amplify/storage';
import { createHazard, createHazardActions} from '../../graphql/mutations';
import dayjs from 'dayjs'
import { Breadcrumb } from './Breadcrumb';
import { GeneralInformation } from './GeneralInformation';
import { Action } from './Action';
/* import { cleanDepartment } from '../../util/details'; */

const emptyFormState = {
    employee: "",
    employeeName: '',
    dateObserved: dayjs().format("YYYY-MM-DD"),
    timeObserved: dayjs().format("h:mm:ss a"),
    businessUnit: '',
    defaultTime: dayjs(),
    defaultDate: dayjs(),
    location: null,
    locationType: '',
    externalLocationType: '',
    externalLocation: '',
    externalCustomer: null,
    country: 'Trinidad and Tobago',
    otherCountry: null,
    observationType: '',
    observationDescription: '',
    immediateActionTaken: '',
    immediateActionTakenDetails: '',
    furtherActionRequired: '',
    actions: [],
    files: [],
    localFiles: [],

}

export const NewHazardView = ({client}) => {
    const {
        userData,
        StyledDiv
    } = useContext(AppContext)

    const scrollableContainerRef = useRef(null);
    const navigate = useNavigate();

    const [formState, setFormState] = useState({
        ...emptyFormState, 
        businessUnit: /* userData['custom:Department'] ? cleanDepartment(userData['custom:Department']) :  */"",
        employeeName: userData.given_name + " " + userData.family_name
    });
    const [submitReady, setSubmitReady] = useState(false);
    const [currentSection, setCurrentSection] = useState(1);
    const [status, setStatus] = useState([
        "process",
        "wait",
    ]);
    const [modalDetails, setModalDetails] = useState({show: false, loading: false, title: "", content: "", error: false});

    const [generalValidations, setGeneralValidations] = useState({
        employee: "",
        employeeName: "",
        businessUnit: "",
        dateObserved: "",
        timeObserved: "",
        location: "",
        locationType: "",
        externalLocationType: "",
        externalCustomer: "",
        country: "",
        otherCountry: "",
        observationType: "",
        observationDescription: "",
    });

    const [actionValidations, setActionValidations] = useState({
        immediateActionTaken: "",
        immediateActionTakenDetails: "",
        furtherActionRequired: "",
        actions: ""
    });

    const handleNextSection = () => {   
        if(currentSection === 1){
            const generalValidated = handleGeneralValidation();
            if(generalValidated){
                setStatus(["finish", "process"]);
                setCurrentSection(currentSection => currentSection + 1);
                setSubmitReady(true);
            }else{
                setStatus(["error", "wait"]);
            }

            scrollToTop();
        }

        else if(currentSection === 2){
            const actionValidated = handleActionValidation();
            if(actionValidated){
                setStatus(["finish", "finish"]);
                scrollToTop();
            }else{
                setStatus(["finish", "error"]);
            }
        }
    };

    const handlePreviousSection = () => {
        if(submitReady)
            setSubmitReady(false);

        if(currentSection === 2){
            setStatus(["finish", "process"]);
        
        }else if(currentSection === 1){
            setStatus(["process", "wait"]);
        }
        setCurrentSection(section => section - 1);
        scrollToTop(); 
    };

    const scrollToTop = () => {
        if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTop = 0;
        }
    };

    const handleSubmit = () => {
        const actionValidated = handleActionValidation();
        if(actionValidated){
            setStatus(["finish", "finish"]);
            setModalDetails(modalData => ({...modalData, show: true, loading: true}))
            submitData();
        }else{
            setStatus(["finish", "error"]);
        }
        
    };

    async function submitData(){
        try{
            const newHazardData = {
                HZDID: "HZD-000",
                employee: formState.employee,
                employeeName: formState.employeeName,
                dateObserved: formState.dateObserved,
                timeObserved: formState.timeObserved,
                locationType: formState.locationType,
                location: formState.location,
                externalLocationType: formState.externalLocationType,
                externalCustomer: formState.locationType === "External" ? formState.externalCustomer : "",
                country: formState.country,
                otherCountry: formState.country === "Other" ? formState.otherCountry : "",
                createdBy: userData.given_name + " " + userData.family_name,
                businessUnit: formState.businessUnit,
                createdByEmail: userData.email,
                status: "open",
                immediateActionTaken: formState.immediateActionTaken,
                immediateActionTakenDetails: formState.immediateActionTakenDetails,
                furtherActionRequired: formState.furtherActionRequired,
                observationType: formState.observationType,
                observationDescription: formState.observationDescription,
                justification: ""
            }

            const newHazard = await client.graphql({
                query: createHazard,
                variables: { input: newHazardData }
            });

            if(formState.furtherActionRequired === "yes"){
                const saveActionPromises = formState.actions.map(action => {
                    let newActionData = {
                        HZDID: newHazard.data.createHazard.id,
                        hazardActionsId: newHazard.data.createHazard.id,
                        action: action,
                        status: "open",
                        sendEmail: false
                    }
    
                    return client.graphql({
                        query: createHazardActions,
                        variables: { input: newActionData }
                    });
                })

                await Promise.all(saveActionPromises);
            }

            saveFilesAndUpdateCategories(newHazard.data.createHazard);
        }catch(e){
            console.log("Error saving Hazard:", e);
            setModalDetails(modalData => ({...modalData, loading: false, title: "Failure", content: "There was an error submitting your Hazard Observation. Please try again later.", error: true}))
        }
    }

    async function saveFilesAndUpdateCategories(newHazard) {
        if (formState.files.length > 0) {
            const savedFilesPromises = formState.files.map(async (file) => {
                const fileExtension = file.name.split('.').pop();
                const timestamp = Date.now();
                const baseFileName = sanitizeFilename(file.name);
                const fileName = `HazardFiles/${newHazard.id}/${baseFileName}_${timestamp}.${fileExtension}`;
        
                try {
                    const result = await uploadData({
                        key: fileName,
                        data: file,
                        options: { level: 'protected' }
                    }).result;
        
                    //console.log('Upload succeeded:', result);
                } catch (error) {
                    console.log('Upload error:', error);
                    setModalDetails(modalData => ({
                        ...modalData,
                        loading: false,
                        title: "Failure",
                        content: "An error occurred while saving your files and attachments. Please contact the IT department for assistance.",
                        error: true
                    }));
                }
            });
        
            // Wait for all file uploads to complete
            await Promise.all(savedFilesPromises);
        
        }
        setModalDetails(modalData => ({...modalData, loading: false, title: "Success", content: "Your Card has been submitted successfully", error: false}))
    }

    function sanitizeFilename(filename, maxLength = 150) {
        const unsafeChars = /[^a-zA-Z0-9._-]/g;
    
        const extensionIndex = filename.lastIndexOf('.');
    
        const baseName = filename.substring(0, extensionIndex);
    
        let sanitizedBaseName = baseName.replace(/ /g, '_').replace(unsafeChars, '');
    
        if (sanitizedBaseName.length > maxLength) {
            sanitizedBaseName = sanitizedBaseName.substring(0, maxLength);
        }
    
        return sanitizedBaseName;
    }

    /* Validation functions */

    const handleGeneralValidation = () => {
        let validated = true;
        const newValidations = {};
        const twoMonthsAgo = dayjs().subtract(2, 'months');
        const today = dayjs().endOf('day');

        if(formState.employee === "") {
            validated = false;
            newValidations.employee = "Please select an option";
        }

        if(formState.employee === "identified" && formState.employeeName === "") {
            validated = false;
            newValidations.employeeName = "Please enter an employee name";
        }

        if(formState.businessUnit === ""){
            validated = false;
            newValidations.businessUnit = "Please select a business unit";
        }

        if (!formState.dateObserved) {
            validated = false;
            newValidations.dateObserved = "Please select a date";
        }

        if(formState.dateObserved && formState.defaultDate.isBefore(twoMonthsAgo)){
            validated = false;
            newValidations.dateObserved = "Date cannot be more than 2 months ago";
        }

        if(formState.dateObserved && formState.defaultDate.isAfter(today)){
            validated = false;
            newValidations.dateObserved = "Date cannot be in the future";
        }
    
        if (!formState.timeObserved) {
            validated = false;
            newValidations.timeObserved = "Please select a time";
        }
    
        if (!formState.location) {
            validated = false;
            newValidations.location = "Please select a location";
        }
    
        if (!formState.locationType) {
            validated = false;
            newValidations.locationType = "Please select a location type";
        }
    
        if (formState.locationType === "External" && !formState.externalLocationType) {
            validated = false;
            newValidations.externalLocationType = "Please select an external location type";
        }
    
        if (formState.locationType === "External" && !formState.location) {
            validated = false;
            newValidations.location = "Please enter an external location";
        }
    
        if (formState.locationType === "External" && !formState.externalCustomer) {
            validated = false;
            newValidations.externalCustomer = "Please select an external customer";
        }
    
        if (!formState.country) {
            validated = false;
            newValidations.country = "Please select a country";
        }
    
        if (formState.country === "Other" && !formState.otherCountry) {
            validated = false;
            newValidations.otherCountry = "Please enter a country";
        }

        if(formState.observationType === ""){
            validated = false;
            newValidations.observationType = "Please select an observation type";
        }

        if(formState.observationDescription === ""){
            validated = false;
            newValidations.observationDescription = "Please enter an observation description";
        }
    
        setGeneralValidations(prevState => ({ ...prevState, ...newValidations }));
    
        return validated;
    };

    const handleActionValidation = () => { 
        let validated = true;
        const newValidations = {};

        if(formState.immediateActionTaken === ""){
            validated = false;
            newValidations.immediateActionTaken = "Please select an immediate action taken";
        }

        if(formState.immediateActionTaken === "yes" && formState.immediateActionTakenDetails === ""){
            validated = false;
            newValidations.immediateActionTakenDetails = "Please enter details of the immediate action taken";
        }

        if(formState.furtherActionRequired === ""){
            validated = false;
            newValidations.furtherActionRequired = "Please select if further action is required";
        }

        if(formState.furtherActionRequired === "yes" && formState.actions.length === 0){
            validated = false;
            newValidations.actions = "Please add at least one further action";
        }

        setActionValidations(prevState => ({ ...prevState, ...newValidations }));

        return validated;
    }

    return (
        <>
            {modalDetails.show && <>
                <div className="modal-overlay">
                    <Row
                        style={{
                            paddingTop: "10dvh",
                            paddingBottom: "20px",
                            height: "100%",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "start"
                        }}
                    >
                        <Col 
                        
                            xl={{span: 10}}
                            lg={{span: 10}}
                            md={{span: 14}}
                            sm={{span: 22}}
                            xs={{span: 22}}
                            style={{height: "33dvh"}}
                        >
                            <div className="modal" style={{padding: "20px"}}>
                                {modalDetails.loading && <Row
                                    style={{
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                        <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} spinning={modalDetails.loading}></Spin>
                                        <StyledDiv>
                                            <p
                                                style={{
                                                    fontSize: "13px",
                                                    color: "rgb(174,32,114)",
                                                    marginTop: "40px"
                                                }}
                                            >Your request is being processed, please wait...</p>
                                        </StyledDiv>
                                    </div>
                                </Row>}

                                {!modalDetails.loading && <>
                                    <Row
                                        style={{
                                            height: "80%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}
                                    >
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                            
                                            {!modalDetails.error && <CheckCircleOutlined style={{ fontSize: 28, color:"#4CAF50" }} />}
                                            {modalDetails.error && <CloseCircleOutlined style={{ fontSize: 28, color:"red" }} />}
                                            <StyledDiv>
                                                <p
                                                    style={{
                                                        margin: "0",
                                                        marginTop: "15px",
                                                        fontSize: "22px",
                                                        fontWeight: "500",
                                                        color: modalDetails.error ? "red" : "#4CAF50",
                                                    }}
                                                >{modalDetails.title}</p>
                                            </StyledDiv>

                                            <StyledDiv>
                                                <p
                                                    style={{
                                                        fontSize: "12px",
                                                        color: modalDetails.error ? "red" : "#4CAF50",
                                                    }}
                                                >{modalDetails.content}</p>
                                            </StyledDiv>
                                        </div>
                                    </Row>

                                    <Row
                                        style={{
                                            display: "flex",
                                            justifyContent: "end",
                                            alignItems: "center",
                                            marginTop: "10px"
                                        }}
                                    >
                                        <Button onClick={() => {
                                            if(modalDetails.error)
                                                setModalDetails(modalData => ({...modalData, show: false}))
                                            else
                                                navigate('/newhome');
                                        }}>Close</Button>                                
                                    </Row>
                                
                                </>}
                                
                            </div>
                        </Col>
                    </Row>
                    
                </div>
            </>}

            <Row
                className="titleHeader"
            >
                <StyledDiv
                    style={{
                        fontSize: "24px",
                        fontWeight: "500",
                        paddingTop: "10px"
                    }}
                >
                    OBSERVATION CARD
                </StyledDiv>
            </Row>

            {userData && <Row
                className="newDataContainer"
            >
                <Col
                    span={24}
                    style={{
                        height: "100%",
                    }}
                >
                    <Row
                        className="dataRow"
                    >
                        <Breadcrumb currentSection={currentSection} status={status}/>

                        <Col
                            xl={{span: 18}}
                            lg={{span: 24}}
                            md={{span: 24}}
                            sm={{span: 24}}
                            xs={{span: 24}}
                            style={{
                                padding: "15px",
                                height: "100%"
                            }}
                        >
                            <Row
                                style={{
                                    height: "90%",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    paddingRight: "20px",
                                    paddingLeft: "20px"
                                }}
                                ref={scrollableContainerRef}
                            >
                                <Col
                                    span={24}
                                >
                                    {currentSection === 1 && <GeneralInformation 
                                        generalValidations={generalValidations} 
                                        setGeneralValidations={setGeneralValidations} 
                                        formState={formState}
                                        setFormState={setFormState}
                                        
                                    />}

                                    {currentSection === 2 && <Action
                                        actionValidations={actionValidations}
                                        setActionValidations={setActionValidations}
                                        formState={formState}
                                        setFormState={setFormState}
                                        setModalDetails={setModalDetails}
                                        
                                    />}
                                </Col>
                                
                            </Row>

                            <Row
                                style={{
                                    height: "10%",
                                }}
                            >
                                <Col
                                    span={12}
                                    style={{
                                        display: "flex",
                                        justifyContent: "start",
                                        alignItems: "end",
                                        height: "100%",
                                    }}
                                >

                                    {currentSection > 1 && <>
                                        <LeftSquareFilled className="buttonIcon" onClick={() => handlePreviousSection()}/>

                                    </>}
                                </Col>

                                <Col
                                    span={12}
                                    style={{
                                        display: "flex",
                                        justifyContent: "end",
                                        alignItems: "end",
                                        height: "100%",
                                    }}
                                >

                                    {!submitReady && <>
                                        <RightSquareFilled className="buttonIcon" onClick={() => handleNextSection()}/>

                                    </>}

                                    {submitReady && <>
                                        <div className="newSubmitContainer"
                                            onClick={() => handleSubmit()}
                                        >
                                            <StyledDiv className="newSubmitText">Submit Observation</StyledDiv>
                                            <CarryOutFilled className="submitIcon" />
                                        </div>

                                    </>}
                                </Col>
                            </Row>
                            
                        </Col>
                    </Row>
                </Col>
            </Row>}
        </>
    )
}
